// Contactos (personas) y Empresas: dos pantallas del menu con la misma forma. Busqueda al instante, filtros en chips y
// llamar con un clic.
import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useEmpresas, useEtapas, useLlamadasRecientes, useOportunidades, usePersonas } from '../data/queries';
import { porTipificar } from '../data/selectors';
import { useDebounced } from '../hooks';
import { ago, isToday, money, plural } from '../lib/format';
import { Icon } from '../lib/icons';
import { looksLikeNumber } from '../lib/phone-number';
import { phone } from '../phone/store';
import { Avatar, Empty, ErrorBox, Skeleton, Tel } from '../ui/bits';
import { abrirDialogo } from '../ui/forms';

export function Contactos({ fijo }: { fijo: 'personas' | 'empresas' }) {
  const [sp, setSp] = useSearchParams();
  const tab = fijo;
  const q = sp.get('q') ?? '';
  const chip = sp.get('f') ?? 'todos';
  const dq = useDebounced(q, 220);
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v);
    else n.delete(k);
    setSp(n, { replace: true });
  };
  // Enlaces viejos: /contactos?tab=empresas
  if (fijo === 'personas' && sp.get('tab') === 'empresas') return <Navigate to={`/empresas${sp.get('q') ? `?q=${encodeURIComponent(sp.get('q')!)}` : ''}`} replace />;

  return (
    <div className="view">
      <header className="vhead">
        <div>
          <h1>{tab === 'personas' ? 'Contactos' : 'Empresas'}</h1>
          <p className="sub">{tab === 'personas' ? 'Las personas del CRM. Toca un número para llamar.' : 'Las empresas del CRM con su gente y sus oportunidades.'}</p>
        </div>
        <div className="vh-act">
          {tab === 'personas' ? (
            <button className="btn" onClick={() => abrirDialogo({ tipo: 'importarContactos' })}>
              <Icon name="download" /> Importar
            </button>
          ) : null}
          <button className="btn primary" onClick={() => abrirDialogo(tab === 'personas' ? { tipo: 'persona' } : { tipo: 'empresa' })}>
            <Icon name="plus" />
            {tab === 'personas' ? 'Nuevo contacto' : 'Nueva empresa'}
          </button>
        </div>
      </header>
      <div className="tools">
        <label className="field">
          <Icon name="search" />
          <input
            value={q}
            onChange={(e) => set('q', e.target.value)}
            placeholder={tab === 'personas' ? 'Nombre, correo, cargo o número' : 'Nombre, ciudad o sitio web'}
            autoComplete="off"
            aria-label="Buscar contactos"
            data-testid="buscar-contactos"
          />
          {q ? (
            <button onClick={() => set('q', '')} aria-label="Limpiar búsqueda">
              <Icon name="x" />
            </button>
          ) : null}
        </label>
      </div>
      {tab === 'personas' ? (
        <Personas q={dq} chip={chip} setChip={(c) => set('f', c === 'todos' ? '' : c)} />
      ) : (
        <Empresas q={dq} chip={chip} setChip={(c) => set('f', c === 'todos' ? '' : c)} />
      )}
    </div>
  );
}

function Personas({ q, chip, setChip }: { q: string; chip: string; setChip: (c: string) => void }) {
  const r = usePersonas(q);
  const llamadas = useLlamadasRecientes();
  // Con un filtro activo se traen todas las paginas: filtrar solo lo cargado daba resultados incompletos.
  useEffect(() => {
    if (chip !== 'todos' && r.hasNextPage && !r.isFetchingNextPage) void r.fetchNextPage();
  }, [chip, r.hasNextPage, r.isFetchingNextPage, r]);
  const nav = useNavigate();
  const ultima = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of llamadas.data ?? []) if (l.personaId && !m.has(l.personaId)) m.set(l.personaId, l.inicio ?? l.creado);
    return m;
  }, [llamadas.data]);
  const pendIds = useMemo(() => new Set(porTipificar(llamadas.data ?? []).map((l) => l.personaId)), [llamadas.data]);
  const todas = r.data?.pages.flatMap((p) => p.items) ?? [];
  const lista = todas.filter((p) => {
    if (chip === 'telefono') return !!p.telefono;
    if (chip === 'hoy') return isToday(ultima.get(p.id));
    if (chip === 'sintip') return pendIds.has(p.id);
    return true;
  });
  const chips: [string, string][] = [
    ['todos', 'Todos'],
    ['telefono', 'Con teléfono'],
    ['hoy', 'Con llamada hoy'],
    ['sintip', 'Sin tipificar'],
  ];
  const total = r.data?.pages[0]?.total;
  return (
    <>
      <div className="chips" role="group" aria-label="Filtros">
        {chips.map(([id, n]) => (
          <button key={id} className="chip" aria-pressed={chip === id} onClick={() => setChip(id)}>
            {n}
          </button>
        ))}
        {total !== undefined ? (
          <span className="note" style={{ alignSelf: 'center', marginLeft: 6 }}>
            {plural(total, 'persona', 'personas')}
          </span>
        ) : null}
      </div>
      {r.error ? <ErrorBox error={r.error} onRetry={() => r.refetch()} /> : null}
      <section className="box" style={{ padding: '6px 10px' }}>
        {r.isLoading ? (
          <Skeleton rows={8} />
        ) : lista.length ? (
          <div className="rows clist">
            {lista.map((p) => (
              <div
                key={p.id}
                className="row click"
                role="link"
                tabIndex={0}
                onClick={() => nav(`/personas/${p.id}`)}
                onKeyDown={(e) => e.key === 'Enter' && nav(`/personas/${p.id}`)}
              >
                <Avatar nombre={p.nombreCompleto} />
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{p.nombreCompleto}</div>
                  <div className="sb">{[p.cargo, p.empresa?.nombre].filter(Boolean).join(' · ') || p.email || '—'}</div>
                </div>
                <span className="cty">{p.email}</span>
                <span className="lst">{ultima.has(p.id) ? ago(ultima.get(p.id)) : '—'}</span>
                <Tel numero={p.telefono} contacto={{ id: p.id, nombre: p.nombreCompleto, empresa: p.empresa?.nombre }} />
              </div>
            ))}
          </div>
        ) : (
          <Empty icon="users" titulo={q ? `Sin resultados para “${q}”` : 'Aún no hay contactos'} texto={chip !== 'todos' ? 'Prueba quitando el filtro.' : undefined}>
            {looksLikeNumber(q) ? (
              <button className="btn ok" onClick={() => phone.dial(q)}>
                <Icon name="phone" />
                Llamar a {q}
              </button>
            ) : (
              <button className="btn" onClick={() => abrirDialogo({ tipo: 'persona' })}>
                <Icon name="plus" />
                Crear contacto
              </button>
            )}
          </Empty>
        )}
        {r.hasNextPage ? (
          <div className="more-row">
            <button className="btn" onClick={() => r.fetchNextPage()} disabled={r.isFetchingNextPage}>
              {r.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}

function Empresas({ q, chip, setChip }: { q: string; chip: string; setChip: (c: string) => void }) {
  const r = useEmpresas(q);
  const opps = useOportunidades();
  const etapas = useEtapas();
  // La etapa ganada es la ultima del embudo (igual en Hoy, Oportunidades y las fichas).
  const ganada = etapas.data?.[etapas.data.length - 1]?.valor;
  useEffect(() => {
    if (chip === 'pipeline' && r.hasNextPage && !r.isFetchingNextPage) void r.fetchNextPage();
  }, [chip, r.hasNextPage, r.isFetchingNextPage, r]);
  const nav = useNavigate();
  const pipe = useMemo(() => {
    const m = new Map<string, { monto: number; moneda: string }>();
    for (const o of opps.data ?? []) {
      if (!o.empresaId || o.etapa === ganada) continue;
      const x = m.get(o.empresaId) ?? { monto: 0, moneda: o.moneda };
      x.monto += o.monto;
      m.set(o.empresaId, x);
    }
    return m;
  }, [opps.data, ganada]);
  const lista = (r.data?.pages.flatMap((p) => p.items) ?? []).filter((e) => chip !== 'pipeline' || pipe.has(e.id));
  return (
    <>
      <div className="chips" role="group" aria-label="Filtros">
        <button className="chip" aria-pressed={chip !== 'pipeline'} onClick={() => setChip('todos')}>
          Todas
        </button>
        <button className="chip" aria-pressed={chip === 'pipeline'} onClick={() => setChip('pipeline')}>
          Con oportunidad abierta
        </button>
      </div>
      {r.error ? <ErrorBox error={r.error} onRetry={() => r.refetch()} /> : null}
      <section className="box" style={{ padding: '6px 10px' }}>
        {r.isLoading ? (
          <Skeleton rows={8} />
        ) : lista.length ? (
          <div className="rows clist">
            {lista.map((e) => (
              <div
                key={e.id}
                className="row click"
                role="link"
                tabIndex={0}
                onClick={() => nav(`/empresas/${e.id}`)}
                onKeyDown={(ev) => ev.key === 'Enter' && nav(`/empresas/${e.id}`)}
              >
                <Avatar nombre={e.nombre} square />
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{e.nombre}</div>
                  <div className="sb">{e.dominio || '—'}</div>
                </div>
                <span className="cty">{e.ciudad || '—'}</span>
                <span className="lst mono">{pipe.has(e.id) ? money(pipe.get(e.id)!.monto, pipe.get(e.id)!.moneda) : '—'}</span>
                <Icon name="chevRight" />
              </div>
            ))}
          </div>
        ) : (
          <Empty icon="building" titulo={q ? `Sin resultados para “${q}”` : 'Aún no hay empresas'}>
            <button className="btn" onClick={() => abrirDialogo({ tipo: 'empresa' })}>
              <Icon name="plus" />
              Crear empresa
            </button>
          </Empty>
        )}
        {r.hasNextPage ? (
          <div className="more-row">
            <button className="btn" onClick={() => r.fetchNextPage()} disabled={r.isFetchingNextPage}>
              {r.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}
